import "./TooltipHost.css";

import {
  type JSX,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { useIslandHost } from "@frontend/contexts";

import { Tooltip } from "./Tooltip";
import { type TooltipDataProps, tooltipDataSchema } from "./Tooltip.schema";
import { TooltipContent } from "./TooltipContent";
import {
  TOOLTIP_CLOSE_DELAY_MS,
  TOOLTIP_DELAY_MS,
  TOOLTIP_TAG_NAME,
} from "./constants";

type TooltipComponentProps = Partial<TooltipDataProps> & {
  ref?: Ref<TooltipElementHandle>;
};

export interface TooltipElementHandle {
  open: () => void;
  close: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [TOOLTIP_TAG_NAME]: {
      props: TooltipComponentProps;
      handle: TooltipElementHandle;
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-tooltip": HTMLElement & TooltipElementHandle;
  }
}

export function readTooltipSlotted(el: HTMLElement): Partial<TooltipDataProps> {
  const anchor = el.firstElementChild;

  const title = anchor?.getAttribute("title");

  if (!title) {
    return {};
  }

  anchor?.removeAttribute("title");

  return { content: el.getAttribute("content") ?? title };
}

function RWCTooltipComponent({
  ref,
  content,
  side,
  sideOffset,
  align,
  alignOffset,
  arrow,
  delay = TOOLTIP_DELAY_MS,
  closeDelay = TOOLTIP_CLOSE_DELAY_MS,
  disabled,
}: TooltipComponentProps): JSX.Element {
  const host = useIslandHost();
  const popupId = useId();

  const [open, setOpen] = useState(false);

  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const anchor = useCallback(
    () => host?.firstElementChild ?? host ?? null,
    [host],
  );

  const settle = useCallback((next: boolean) => {
    clearTimeout(timer.current);
    setOpen(next);
  }, []);

  const schedule = useCallback((next: boolean, ms: number) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(next), ms);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      open: () => settle(true),
      close: () => settle(false),
    }),
    [settle],
  );

  useEffect(() => {
    if (!host) {
      return;
    }

    const handlePointerEnter = () => {
      if (!disabled) {
        schedule(true, delay);
      }
    };

    const handlePointerLeave = () => schedule(false, closeDelay);

    const handleFocusIn = (event: FocusEvent) => {
      if (!disabled && (event.target as Element).matches?.(":focus-visible")) {
        settle(true);
      }
    };

    const handleDismiss = () => settle(false);

    host.addEventListener("pointerenter", handlePointerEnter);
    host.addEventListener("pointerleave", handlePointerLeave);
    host.addEventListener("focusin", handleFocusIn);
    host.addEventListener("focusout", handleDismiss);
    host.addEventListener("click", handleDismiss);

    return () => {
      clearTimeout(timer.current);

      host.removeEventListener("pointerenter", handlePointerEnter);
      host.removeEventListener("pointerleave", handlePointerLeave);
      host.removeEventListener("focusin", handleFocusIn);
      host.removeEventListener("focusout", handleDismiss);
      host.removeEventListener("click", handleDismiss);
    };
  }, [host, disabled, delay, closeDelay, schedule, settle]);

  useEffect(() => {
    const element = anchor();

    if (!open || !element) {
      return;
    }

    element.setAttribute("aria-describedby", popupId);

    return () => element.removeAttribute("aria-describedby");
  }, [anchor, open, popupId]);

  return (
    <Tooltip open={open && !disabled && !!content} onOpenChange={settle}>
      <slot />

      <TooltipContent
        id={popupId}
        anchor={anchor}
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        arrow={arrow}
      >
        {content}
      </TooltipContent>
    </Tooltip>
  );
}

componentManager.register(
  rwc(TOOLTIP_TAG_NAME, {
    component: RWCTooltipComponent,
    dataSchema: tooltipDataSchema,
    usesSlots: true,
    readSlotted: readTooltipSlotted,
  }),
);
